import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import Wallpaper from '../../wallpaper-bundle';
import { ErrorBoundary } from '@wetransfer/transfer/adsdk/error-boundary';
import MobileWebMicroFrontend from '@wetransfer/transfer/adsdk/mobile-web-micro-frontend';
import { returnedFromSSO } from '@wetransfer/transfer/auth-bundle/helpers/sso';
import { AppLauncher } from '@wetransfer/transfer/component/AppLauncher/AppLauncher';
import BannerDiscount from '@wetransfer/transfer/component/BannerDiscount/BannerDiscount';
import CCpaBanner from '@wetransfer/transfer/component/ccpa';
import { isConsentActivated } from '@wetransfer/transfer/component/consent-manager';
import { isCcpaFlagActive } from '@wetransfer/transfer/component/consent-manager/flags';
import CookieBanner from '@wetransfer/transfer/component/cookie-banner';
import Dropzone from '@wetransfer/transfer/component/DropZone/DropZone';
// Load the main components for our application
import Footer from '@wetransfer/transfer/component/footer';
import Logo from '@wetransfer/transfer/component/logo';
import Navigation from '@wetransfer/transfer/component/navigation';
import NavigationConfig from '@wetransfer/transfer/component/navigation/config';
import MobileNavigation from '@wetransfer/transfer/component/navigation/mobile';
import NoticeBar from '@wetransfer/transfer/component/noticebar';
import Panel from '@wetransfer/transfer/component/panel';
import Splashpage from '@wetransfer/transfer/component/splashpage';
import Sticky from '@wetransfer/transfer/component/sticky';
import TransferWindow from '@wetransfer/transfer/component/TransferWindow';
import Welcome, { STEP_COOKIES } from '@wetransfer/transfer/component/welcome';
import { getCookieWallVariation } from '@wetransfer/transfer/component/welcome/cookie-wall-variations-util';
import ExperimentDiscountBanner from '@wetransfer/transfer/constant/experiments/discount-banner';
import ExperimentDiscountModal from '@wetransfer/transfer/constant/experiments/discount-modal';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Helper from '@wetransfer/transfer/helper';
import SplashpageHelper from '@wetransfer/transfer/helper/splashpage';
import { userHasPaidTier } from '@wetransfer/transfer/helper/user';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import { fetchWallpapers as fetchChannelWallpapers } from '@wetransfer/transfer/store/action/channel';
import { isInChannel } from '@wetransfer/transfer/store/selector/channel';

// Keep track of our component states and route, this is the
// way we'll make sure multi-component routing works.
const componentStates = {};
const componentRoutes = {};

const rootNode = document.querySelector('.root-node');
// This is needed so screen readers don't see main content when modal is opened.
Modal.setAppElement(rootNode);

export class Layout extends React.Component {
  state = {
    hasFetchedChannelWallpapers: false,
    isFetchingChannelWallpapers: false,
    isSplashPageOpen: false,
    hasSplashPageMounted: false,
  };

  // When receiving props let's take a look at what needs to happen.
  static getDerivedStateFromProps(props, state) {
    if (!props.route) {
      return;
    }

    // Update the component state and route for this route type.
    const { type, updateTransfer } = props.route;

    // We keep the transfer id + secret when moving to advertise/wallpaper because it prevents the component from losing its state
    const advert_id = props.route.params.advert_id;

    if (advert_id) {
      props.route.params = { ...componentStates[type], advert_id };
    }

    componentStates[type] = props.route.params;
    componentRoutes[type] = props.route;

    // Explicitly update the state of the transfer for some panel routes.
    if (updateTransfer) {
      componentStates.transfer = props.route.params;
      componentRoutes.transfer = props.route;
    }

    return state;
  }

  componentDidUpdate() {
    const { isInChannel, channelName, fetchChannelWallpapers } = this.props;

    if (
      isInChannel &&
      !this.state.isFetchingChannelWallpapers &&
      !this.state.hasFetchedChannelWallpapers
    ) {
      this.setState({ isFetchingChannelWallpapers: true }, async () => {
        await fetchChannelWallpapers({ name: channelName });
        this.setState({ hasFetchedChannelWallpapers: true });
        this.setState({ isFetchingChannelWallpapers: false });
      });
    }
  }

  renderWallpaperComponent() {
    return (
      <Suspense fallback={null}>
        <Wallpaper />
      </Suspense>
    );
  }

  // We'll draw our main components
  render() {
    const {
      session,
      transfer,
      transferUpload,
      user,
      consent,
      isLoading,
      pending,
      auth,
      isMobileWebWallpaperEnabled,
    } = this.props;
    const className = 'app application';
    const transferProps = componentStates['transfer'] || {};
    const route = { ...this.props.route };
    const incompatibleVersion = session.incompatible_version;

    const transferPasswordNeeded =
      route.requiresTransferPassword &&
      (!transfer ||
        !transfer.state ||
        (!transfer.password && transfer.state === 'password_protected'));
    let iconComponent = null;
    let navComponent = null;
    let content = null;
    let wallpaperComponent = null;
    let transferWindowComponent = null;
    let footerComponent = null;
    let welcome = null;
    let cookieBanner = null;
    let ccpaBanner = null;
    let bannerDiscount = null;

    const { text, color, onClose, showCloseIcon } = Helper.getNoticeOptions({
      incompatibleVersion,
      user,
    });

    const noticeOptions = {
      text,
      color,
      route,
      transferNode: this.transferNode,
      transferState: transfer && transfer.state,
      onClose,
      showCloseIcon,
      alignedToAppLauncher: auth.isLoggedIn,
    };
    const noticeBar = noticeOptions.text && <NoticeBar {...noticeOptions} />;

    // Don't show the panel if the route requires TOC but hasn't been accepted
    if ((route.requiresTOC && !session.tandc) || transferPasswordNeeded) {
      route.type = 'transfer';
    }

    // If the current route is a page, we'll display it accordingly.

    if (route.raw) {
      const RawContent = route.component;
      content = <RawContent />;
    } else if (route.isPanelOpen) {
      content = (
        <Panel
          key="panel"
          locale={session.language}
          isLoading={isLoading}
          {...componentStates[route.type]}
        />
      );
    }

    const setSplashPageOpenState = (isOpen) => {
      this.setState({ isSplashPageOpen: isOpen });
    };

    const setHasSplashPageMountedState = () => {
      this.setState({ hasSplashPageMounted: true });
    };

    const hasConsentFeature = isConsentActivated({
      user,
      session,
      consent,
      route,
    });

    const currentPath = window.location.pathname;

    const isCheckoutFlow =
      currentPath === '/upgrade' ||
      currentPath === '/payment/methods' ||
      currentPath === '/sign-up';

    const isWallpaperURL = currentPath.includes('/wallpaper/');

    const search = window.location.search;
    const isWallpaperQuery = new URLSearchParams(search).get('ad_id');

    const showDiscountBanner =
      ExperimentDiscountBanner.isInitialised() &&
      ExperimentDiscountBanner.isShowDiscountBanner() &&
      ExperimentDiscountBanner.payload &&
      !userHasPaidTier(user) &&
      !Browser.isMobile &&
      !isLoading &&
      !isCheckoutFlow &&
      !isWallpaperURL &&
      !isWallpaperQuery;

    if (showDiscountBanner) {
      cookieBanner = null;
      bannerDiscount = (
        <BannerDiscount payload={ExperimentDiscountBanner.payload} />
      );
    }

    // Show the cookies wall if cookie preference is not set
    if (hasConsentFeature) {
      if (!session.privacy.allowTracking && !session.privacy.notified) {
        cookieBanner = <CookieBanner tandcAccepted={session.tandc} />;
      } else {
        cookieBanner = null;
      }

      if (!Browser.isMobile) {
        cookieBanner = null;
        const cookieWallVariation = getCookieWallVariation();
        welcome = (
          <Welcome
            step={STEP_COOKIES}
            cookieWallVariation={cookieWallVariation}
          />
        );
        wallpaperComponent = null;
      }
    } else if (!Browser.isMobile) {
      // Don't show Cookie wall, show CCPA banner
      if (isCcpaFlagActive()) {
        cookieBanner = null;
        ccpaBanner = <CCpaBanner />;
      }
    }

    if (!session.tandc) {
      cookieBanner = null;
    }

    // Mobile specific components
    if (Browser.isMobile) {
      navComponent = route.raw ? null : (
        <Sticky>
          <MobileNavigation
            menuItems={NavigationConfig.getItems()}
            route={route}
            isLoading={isLoading}
            user={user}
            isLogoutPending={pending.signout}
            auth0UserId={auth.auth0UserId}
          />
        </Sticky>
      );
      footerComponent = route.raw ? null : <Footer />;
    } else {
      wallpaperComponent = hasConsentFeature
        ? null
        : this.renderWallpaperComponent();

      const showSpinnerLogo =
        !user.loggedIn ||
        (transferUpload.progress.percentage > 0 &&
          (route.type === 'page' ||
            (route.type === 'plus' &&
              window.innerWidth <= AppConfig.panel.full_panel_breakpoint)));
      iconComponent = showSpinnerLogo ? (
        <Logo
          route={route}
          progressPercentage={transferUpload.progress.percentage}
        />
      ) : (
        <AppLauncher />
      );
      navComponent = (
        <Navigation
          systemNotification={noticeOptions.text}
          isLoading={isLoading}
        />
      );
      transferWindowComponent = (
        <TransferWindow
          ref={(node) => (this.transferNode = node)}
          route={route}
          {...transferProps}
        />
      );
    }

    if (this.props.isInChannel && !this.state.hasFetchedChannelWallpapers) {
      wallpaperComponent = null;
    }

    // When pro users are coming from SSO when logging in, we don't know as
    // the app loads if we have a pro user or not. And we don't want to render
    // the wallpaper until we have auth data (so `isLoading = false`) to avoid
    // showing ads to pro users. This does affect free account users logging in,
    // in which case we will also delaying showing an ad. Free account users
    // see ads. This doesn't affect logged out users at all.
    const userHasReturnedFromSSO = returnedFromSSO();

    // We should hide the wallpaper if the user has returned from SSO and the page is loading
    // We should also hide the wallpaper until we know for certain the splash page was mounted
    // and the logic for showing it has been checked, this ensures the wallpaper rotation does not
    // start before the splash page is mounted

    const modalShowing =
      ExperimentDiscountModal.isShowDiscountModal() &&
      SplashpageHelper.shouldDisplaySplashpage() &&
      isLoading;

    const shouldHideWallpaper =
      (userHasReturnedFromSSO && isLoading) ||
      this.state.isSplashPageOpen ||
      !this.state.hasSplashPageMounted ||
      modalShowing;

    // We should hide the transfer window if the page is loading
    // We also should hide the transfer window if the splash page component is active
    // (for accessibility purposes)
    const shouldHideTransferWindow =
      isLoading ||
      this.state.isSplashPageOpen ||
      !this.state.hasSplashPageMounted;

    let getClassNames;

    if (showDiscountBanner) {
      const banner = document.querySelector('.Banner__wrapper');
      const bannerHeight = banner?.clientHeight;
      getClassNames = { height: `calc(100% - ${bannerHeight}px)` };
    }

    return (
      <>
        <div className={className} style={getClassNames}>
          {iconComponent}

          <TransitionGroup appear={true} component={null}>
            {!isLoading && (
              <Splashpage
                onVisible={setSplashPageOpenState}
                onMount={setHasSplashPageMountedState}
              />
            )}
            {noticeBar}
            {Browser.isMobile && isMobileWebWallpaperEnabled && (
              <ErrorBoundary>
                <MobileWebMicroFrontend />
              </ErrorBoundary>
            )}
            {content}
            {welcome}
            {shouldHideTransferWindow ? null : transferWindowComponent}
            {
              // The dropzone has been moved in to the Pro Transfer Window
              // so we do not show it when the experiment is active
              <Dropzone
                disabled={!session.tandc || Browser.isMobile || isLoading}
              />
            }
            {shouldHideWallpaper ? null : wallpaperComponent}
            {navComponent}
            {footerComponent}
            {cookieBanner}
            {ccpaBanner}
          </TransitionGroup>
        </div>
        {
          // this needs to exist outside the other components
        }
        {bannerDiscount}
      </>
    );
  }
}

Layout.propTypes = {
  route: PropTypes.object,
  session: PropTypes.object,
  channelName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    .isRequired,
  isInChannel: PropTypes.bool.isRequired,
  fetchChannelWallpapers: PropTypes.func.isRequired,
  transfer: PropTypes.object,
  transferUpload: PropTypes.object,
  user: PropTypes.object,
  consent: PropTypes.object,
  account: PropTypes.object,
  isLoading: PropTypes.bool,
  pending: PropTypes.object,
  auth: PropTypes.object,
  isMobileWebWallpaperEnabled: PropTypes.bool,
};

const mapStateToProps = (
  {
    channel,
    consent,
    session,
    transfer,
    transferUpload,
    user,
    account,
    pending,
    auth,
  },
  { isMobileWebWallpaperEnabled }
) => ({
  isMobileWebWallpaperEnabled,
  isInChannel: isInChannel({ channel }),
  channelName: channel.name,
  session,
  transfer,
  transferUpload,
  user,
  consent,
  account,
  pending,
  auth,
});

const mapDispatchToProps = {
  fetchChannelWallpapers,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
  pure: false,
})(Layout);
